import { Injectable } from '@angular/core';
import { StringHelpers } from '@mode/shared/util-js';
import _ from 'lodash';
import { ModeModelBase } from '../model';
import { ModeCollectionFactoryService } from '../model-collection.factory';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';
import { ChartModelAccessor } from './chart';

export class QueryModel extends ModeModelBase {
  last_run_id: string;
  override _name = 'query';
  limit: any;
  initials: any;
  name: string;
  formatted_query: any;
  formatted: boolean;
  original_query: any;
  raw_query: any;
  formatDelayOn: boolean;
  override idAttribute = 'token';
  override belongsTo = 'report';
  override parentIdAttribute = 'username';
  override hasMany = [{ name: 'queryRuns', idAttribute: 'token' }, { name: 'charts' }, { name: 'queryTables' }];
  override attributes = [
    { name: 'token', default: null },
    { name: 'raw_query', default: '' },
    { name: 'created_at', default: '' },
    { name: 'limit', default: true, abstract: true },
    { name: 'name', default: '' },
    { name: 'last_run_id', default: '' },
    { name: 'data_source_id', default: null },
    { name: 'encoding', default: null, type: 'text' },
  ];
  override initialize(resource) {
    // When a report is cloned after a failed run, its queries have no runs.
    // In that case, set the default limit of the queries to true.
    if (!this.last_run_id && this.limit === undefined) {
      this.limit = true;
      resource.limit = true;
    }
    this.initials = StringHelpers.initialize(this.name || 'Query');
  }
  updateInitials() {
    this.initials = StringHelpers.initialize(this.name || 'Query');
  }
  formatUpdate(new_query) {
    this.formatted_query = new_query;
    this.formatted = true;
  }
  formatUndo() {
    this.formatted = false;
  }
  formatUndoable() {
    if (this.original_query == this.formatted_query && this.formatted == false) {
      // deal with stupid case where the format didn't actually change the query
      return false;
    } else {
      return this.original_query && this.formatted_query && this.formatted_query == this.raw_query;
    }
  }
  formatCacheQuery() {
    this.original_query = this.raw_query;
  }
  formatCached() {
    return this.formatted_query && this.original_query && this.original_query == this.raw_query;
  }
  formatRestoreCache() {
    this.formatted = true;
  }
  formatDisabled() {
    return (
      !this.raw_query.trim() || // disable empty query
      (this.formatDelayOn && !this.formatUndoable())
    ); // only delay format, not undo
  }
  formatSetDelay() {
    this.formatDelayOn = true;
  }
  formatClearDelay() {
    this.formatDelayOn = false;
  }
}

@Injectable({ providedIn: 'root' })
export class QueryModelAccessor extends ModelAccessorBase<QueryModel> {
  override name = 'query';
  override idAttribute = 'token';
  override parentModel = 'report';
  override parentIdAttribute = 'token';
  override createAttributes = ['report'];
  createModel(resource, opts?) {
    const model = new QueryModel(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }
  constructor(
    injectables: ModelAccessorInjectables,
    private ChartModel: ChartModelAccessor,
    private ModeCollection: ModeCollectionFactoryService
  ) {
    super(injectables, 'migrated', 'mode.query');
  }

  // helper functions
  buildQueriesWithVisualizations(queries) {
    return this.injectables.$q.all(_.map(queries, (query) => this.buildVisualizations(query)));
  }
  private buildVisualizations(query) {
    const deferred = this.injectables.$q.defer();
    // get charts and tables in parallel
    this.injectables.$q
      .all([this.getCharts(query), this.getTables(query)])
      .then(([charts, tables]) => {
        query.storedCharts = charts;
        query.storedTables = tables;
        deferred.resolve(query);
      })
      .catch(deferred.reject);
    return deferred.promise;
  }

  private getCharts(query) {
    return query
      .charts()
      .then((collection) => this.ModeCollection.elements(collection))
      .then((charts) => this.ChartModel.buildChartsWithColorPalettes(charts))
      .then((charts) => {
        // make sure to save parent token for later reference
        _.each(charts, (chart) => {
          chart.parent = query.token;
        });
        return charts;
      });
  }

  private getTables(query) {
    return query
      .queryTables()
      .then((collection) => this.ModeCollection.elements(collection))
      .then((tables) => {
        // make sure to save parent token for later reference
        _.each(tables, (table) => {
          table.parent = query.token;
        });
        return tables;
      });
  }
}
